import maplibregl from 'maplibre-gl'
import {postRoutePlan} from '@/crud/senders'
import {emptyFeatureCollection} from '@/services/map/route-geometry'

/** MapLibre source/layer id for the planned (suggested) route. */
export const SRC_PLANNED_ROUTE = 'planned-route'

export interface PlannedRoute {
  /** GeoJSON LineString in MapLibre order: [lng, lat]. */
  geometry: {type: 'LineString'; coordinates: [number, number][]}
  /** Kilometers. */
  distance: number
  /** Seconds. */
  duration: number
}

export interface RoutePlanPoint {
  lat: number
  lng: number
}

/** Heavy work (routing) happens on the backend; this is just the API call. */
export async function planRoute(points: RoutePlanPoint[]): Promise<PlannedRoute> {
  const response = await postRoutePlan(points)
  return response.data as PlannedRoute
}

export function ensurePlannedRouteLayer(map: maplibregl.Map): void {
  if (map.getSource(SRC_PLANNED_ROUTE)) return

  map.addSource(SRC_PLANNED_ROUTE, {type: 'geojson', data: emptyFeatureCollection() as any})
  // White casing under a blue line keeps the suggestion readable on any basemap.
  map.addLayer({
    id: `${SRC_PLANNED_ROUTE}-casing`,
    type: 'line',
    source: SRC_PLANNED_ROUTE,
    layout: {'line-join': 'round', 'line-cap': 'round'},
    paint: {'line-color': '#ffffff', 'line-width': 8}
  })
  map.addLayer({
    id: SRC_PLANNED_ROUTE,
    type: 'line',
    source: SRC_PLANNED_ROUTE,
    layout: {'line-join': 'round', 'line-cap': 'round'},
    paint: {'line-color': '#1a73e8', 'line-width': 4, 'line-dasharray': [2, 1]}
  })
}

export function setPlannedRouteData(map: maplibregl.Map, route: PlannedRoute): void {
  const source = map.getSource(SRC_PLANNED_ROUTE) as maplibregl.GeoJSONSource | undefined
  source?.setData({type: 'Feature', properties: {}, geometry: route.geometry} as any)
}

export function clearPlannedRouteData(map: maplibregl.Map): void {
  const source = map.getSource(SRC_PLANNED_ROUTE) as maplibregl.GeoJSONSource | undefined
  source?.setData(emptyFeatureCollection() as any)
}

/** "1 h 25 min" / "12 min" from seconds. */
export function formatRouteDuration(seconds: number): string {
  const minutes = Math.round(seconds / 60)
  const hours = Math.floor(minutes / 60)
  return hours > 0 ? `${hours} h ${minutes % 60} min` : `${minutes} min`
}
